import { useWallet } from "@manahippo/aptos-wallet-adapter";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import Button from "../../components/Common/Button";
import Card from "../../components/Common/Card";
import TransactionDialog from "../../components/Common/TransactionDialog";
import LoginWallet from "../../components/LoginWallet";
import InputDateInCard from "../../components/Vest/InputDateInCard";
import InputInCard from "../../components/Vest/InputInCard";
import { getTransactionByHash, getNowSeconds } from "../../libs/aptos_sdk";
import coins from "../../libs/coins/coins.json";
import {
  increase_amount_vest,
  increase_duration_vest,
  withdraw_vest,
} from "../../libs/swap_sdk";
import { getLockedBalance } from "../../libs/vesting_escrow";
import { sleep } from "../../utils/common";

export default function ManageVest() {
  const { token_id } = useParams();
  const inRef = React.createRef();
  const { signAndSubmitTransaction, account } = useWallet();
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [increaseLockBtnDisable, setIncreaseLockBtnDisable] =
    useState<boolean>(true);
  const [increaseAmountBtnText, setIncreaseAmountBtnText] =
    useState<string>("Enter an amount");

  const [step, setStep] = useState<number>(0);
  const [inSymbol] = ["FOIL"];
  const [inAmount, setInAmount] = useState("0");
  const [inCoin] = useState<string>(coins[4].address); //foil coin
  const [txn, setTxn] = useState<string>("");
  const now = Math.floor(new Date().getTime() / 1000);
  const [lockDuration, setLockDuration] = useState<number>(now + 7 * 86400);
  const [lockedAmount, setLockedAmount] = useState<number>(0);
  const [canWithdraw, setCanWithdraw] = useState<boolean>(false);
  const [expiredString, setExpiredString] = useState<string>("");
  const [lockingEnd, setLockingEnd] = useState<number>(now);
  useEffect(() => {
    const getTokenInfo = async () => {
      const locked_balance = await getLockedBalance(parseInt(token_id!, 10));
      const locking_end = locked_balance.end;
      const chainNow = await getNowSeconds();
      setLockingEnd(parseInt(locking_end, 10));
      if (locking_end < chainNow) {
        const duration = chainNow - locking_end;
        if (duration / 86400 <= 1) {
          setExpiredString("1 day");
        } else {
          setExpiredString(`${Math.ceil(duration / 86400)} days`);
        }
      }

      setCanWithdraw(locking_end < chainNow);

      setLockedAmount(locked_balance.amount.value / 1000000000);
    };
    getTokenInfo();
  }, [token_id]);

  useEffect(() => {
    if (inRef && inRef.current) {
      // @ts-ignore
      const balanceIn = inRef.current.balance;
      if (Number(inAmount) > Number(balanceIn)) {
        setIncreaseLockBtnDisable(true);
        setIncreaseAmountBtnText("Insufficient in coin balance");
      } else if (Number(inAmount) > 0) {
        setIncreaseLockBtnDisable(false);
        setIncreaseAmountBtnText("Increase Lock Amount");
      } else {
        setIncreaseLockBtnDisable(true);
        setIncreaseAmountBtnText("Enter an amount");
      }
    }
  }, [inAmount, inRef]);
  const formatDate = (d: Date): string => {
    return `${d.getFullYear()} / ${d.getMonth() + 1} / ${d.getDate()}`;
  };

  const handleCloseAlert = () => setOpenAlert(false);

  const handle_increase_amount = async () => {
    setTxn("");
    setOpenAlert(true);
    setStep(0);
    const data = await increase_amount_vest(
      signAndSubmitTransaction,
      parseInt(token_id!, 10),
      Number(inAmount) * Math.pow(10, 9)
    );

    setTxn(data.hash);
    setStep(1);
    let transaction;
    while (!transaction) {
      try {
        transaction = await getTransactionByHash(data.hash);
      } catch {
        await sleep(500);
      }
    }
    setStep(2);
  };

  const handle_withdraw = async () => {
    setTxn("");
    setOpenAlert(true);
    setStep(0);

    const data = await withdraw_vest(
      signAndSubmitTransaction,
      parseInt(token_id!, 10)
    );

    setTxn(data.hash);
    setStep(1);
    let transaction;
    while (!transaction) {
      try {
        transaction = await getTransactionByHash(data.hash);
      } catch {
        await sleep(500);
      }
    }
    setStep(2);
  };

  const handle_increase_duration = async () => {
    setTxn("");
    setOpenAlert(true);
    setStep(0);
    const lock = lockDuration - now;

    const data = await increase_duration_vest(
      signAndSubmitTransaction,
      parseInt(token_id!, 10),
      lock
    );

    setTxn(data.hash);
    setStep(1);
    let transaction;
    while (!transaction) {
      try {
        transaction = await getTransactionByHash(data.hash);
      } catch {
        await sleep(500);
      }
    }
    setStep(2);
  };

  const handle_in_amount = async (v: any) => {
    setInAmount(v);
  };

  const changeDateOption = (e: any, value: string) => {
    if (value === "1 week") {
      setLockDuration(now + 7 * 86400);
    } else if (value === "1 month") {
      setLockDuration(now + 30 * 86400);
    } else if (value === "1 year") {
      setLockDuration(now + 365 * 86400);
    } else if (value === "4 years") {
      setLockDuration(now + 4 * 365 * 86400);
    }
  };

  return (
    <div className="overflow-auto no-scrollbar flex items-center justify-center h-screen">
      <div className="flex flex-col w-2/5 max-2xl:w-2/3 max-xl:w-full px-4">
        <Card>
          <div className="flex justify-between items-center py-4 px-4">
            <div className="text-white text-xl font-normal">
              Manage Existing Lock
            </div>
          </div>
          {!canWithdraw && (
            <>
              <div className="flex justify-between items-center p-4 pt-0 max-sm:flex-col gap-2">
                <InputInCard
                  inCoin={inCoin}
                  changeAmount={handle_in_amount}
                  onRef={inRef}
                />
              </div>
              <div className="flex justify-between items-center p-4 pt-0 max-sm:flex-col gap-2">
                {account ? (
                  <Button
                    disabled={increaseLockBtnDisable}
                    onClick={handle_increase_amount}
                    variant="contained"
                    fullWidth
                  >
                    {increaseAmountBtnText}
                  </Button>
                ) : (
                  <LoginWallet local={1} />
                )}
              </div>

              <div className="flex justify-between items-center p-4 pt-0 max-sm:flex-col gap-2">
                <InputDateInCard
                  setLockDuration={setLockDuration}
                  lockDuration={lockDuration}
                />
              </div>
              <div className="flex justify-between items-center p-4 pt-0 max-sm:flex-col gap-2">
                <div className="text-white font-bold">Expires</div>
                <div className="flex gap-5 max-sm:gap-3 max-sm:flex-wrap">
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    className="text-sm"
                    defaultValue="1 week"
                    onChange={changeDateOption}
                  >
                    <FormControlLabel
                      value="1 week"
                      control={<Radio size="small" />}
                      label="1 week"
                      className="text-white"
                    />
                    <FormControlLabel
                      value="1 month"
                      control={<Radio size="small" />}
                      label="1 month"
                      className="text-white"
                    />
                    <FormControlLabel
                      value="1 year"
                      control={<Radio size="small" />}
                      label="1 year"
                      className="text-white"
                    />
                    <FormControlLabel
                      value="4 years"
                      control={<Radio size="small" />}
                      label="4 years"
                      className="text-white"
                    />
                  </RadioGroup>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 pt-0 max-sm:flex-col gap-2">
                {!!account && (
                  <Button
                    onClick={handle_increase_duration}
                    variant="contained"
                    fullWidth
                  >
                    Increase Duration
                  </Button>
                )}
              </div>
              <div className="flex justify-between items-center py-4 px-4">
                <div className="flex-col gap-2">
                  <div className="text-white text-sm">
                    Your voting power will be
                  </div>
                  <div className="text-white text-xl font-semibold">
                    0 veNFT
                  </div>
                </div>
                <div className="flex-col gap-2">
                  <div className="text-opacity-50 text-sm text-white">
                    0.00 FoilCoin locked expires in 7 days
                  </div>
                  <div className="text-opacity-50 text-sm text-white">
                    Locked until {formatDate(new Date(lockingEnd * 1000))}
                  </div>
                </div>
              </div>
              <div className="h-px mx-4 bg-white opacity-10" />
              <div className="py-4 px-4 text-sm text-white text-opacity-50">
                <div>1 FoilCoin locked for 4 years = 1.00 veNFT</div>
                <div>1 FoilCoin locked for 3 years = 0.75 veNFT</div>
                <div>1 FoilCoin locked for 2 years = 0.50 veNFT</div>
                <div>1 FoilCoin locked for 1 years = 0.25 veNFT</div>
              </div>
            </>
          )}
          {canWithdraw && (
            <>
              <div className="flex justify-between items-center py-4 px-4">
                <div className="flex-col gap-2">
                  <div className="text-white text-sm">
                    Your current voting power is
                  </div>
                  <div className="text-white text-xl font-semibold">
                    0.00 veNFT
                  </div>
                </div>
                <div className="flex-col gap-2">
                  <div className="text-opacity-50 text-sm text-white">
                    {lockedAmount.toFixed(2)} FoilCoin locked expires{" "}
                    {expiredString} ago
                  </div>
                  <div className="text-opacity-50 text-sm text-white">
                    Locked until {formatDate(new Date(lockingEnd * 1000))}
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 pt-0 max-sm:flex-col gap-2">
                {!!account && (
                  <Button
                    onClick={handle_withdraw}
                    variant="contained"
                    fullWidth
                  >
                    Withdraw
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
      <div>
        <TransactionDialog
          openAlert={openAlert}
          txn={txn}
          title={
            canWithdraw
              ? `Withdraw ${lockedAmount.toFixed(2)} ${inSymbol}`
              : `Increase ${inAmount}  ${inSymbol}`
          }
          step={step}
          handleCloseAlert={handleCloseAlert}
        />
      </div>
    </div>
  );
}
